import { useContext, useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"
import HelloSign from "hellosign-embedded"

//SOAR components:
import EnterpriseLibrarySidebar from "../../../components/sidebars/EnterpriseLibrarySidebar"
import PageSidebarContent from "../../../components/sidebars/PageSidebarContent"
import { LoaderMedium } from "../../../components/Loaders/index"
import { UserContext } from "../../../context/userContext"

//prime react
import { Accordion, AccordionTab } from "primereact/accordion"
import { Tooltip } from "primereact/tooltip"
import { confirmDialog } from "primereact/confirmdialog"

//document signing components
import TemplateFilters from "./TemplateFilters"
import {
  docSigningApiListTemplatesPost,
  docSigningApiHelloSignConfigGet,
  docSigningApiEditTemplateGet,
  docSigningApiDeleteTemplateGet,
} from "../documentsApi"
import NewTemplateForm from "./NewTemplateForm"
import TemplateViewCard from "./TemplateVIewCard"
import TemplateViewTable from "./TemplateViewTable"
import TemplateInfoDialog from "./TemplateInfoDialog"
import EditTemplateGroups from "./EditTemplateGroups"
import { GET_ORGANIZATIONS } from "../queries"

//styles
import docSignStyles from "../../../styles/documentSigning/DocSign.filters.module.css"

//helloSign
import { useClientMethod, useHub } from "react-use-signalr"
import {
  setupSignalRConnection,
  handleHelloSignEvent,
} from "../documentsNotifications"
import { useQuery } from "@apollo/client"
import { LogError } from "../../../helpers/logger"

export default function DocumentsTemplateManagement() {
  //Retrieve relevant context values
  const userCtx = useContext(UserContext)

  const templateSortOptions = ["Creation date", "Template name", "Creator name"]

  const [loading, setLoading] = useState(true)
  const [helloSignConfig, setHelloSignConfig] = useState(undefined)
  const [templates, setTemplates] = useState(null)
  const [appliedFilters, setAppliedFilters] = useState({
    dateCreated: null,
    search: "",
    sort: templateSortOptions[0],
    sortDirection: "Descending",
  })
  const [layoutOption, setLayoutOption] = useState("grid")
  const [showNewTemplate, setShowNewTemplate] = useState(false)
  const [showTemplateInfo, setShowTemplateInfo] = useState(false)
  const [showOrganizationEdit, setShowOrganizationEdit] = useState(false)
  const [templateInfo, setTemplateInfo] = useState(undefined)
  const [organizations, setOrganizations] = useState(undefined)
  const [loadingOrganizations, setLoadingOrganizations] = useState(true)
  const [deleteTemplateIds, setDeleteTemplateIds] = useState([])
  //helloSign
  const [helloSignConnection, setHelloSignConnection] = useState(null)
  const { hubConnectionState, error: hubError } = useHub(helloSignConnection)

  const topScroll = useRef()

  useQuery(GET_ORGANIZATIONS, {
    variables: { staff_id: userCtx.session.user.staff_id },
    fetchPolicy: "network-only",
    onCompleted: ({ organizations }) => {
      setOrganizations(organizations)
      setLoadingOrganizations(false)
    },
    onError: (error) => {
      userCtx.setNotificationError("Unable to load organizations")
      LogError("Unable to load organizations", error)
      setOrganizations(undefined)
      setLoadingOrganizations(false)
    },
  })

  async function GetTemplates(page, pageSize, filters) {
    setLoading(true)
    try {
      const useFilters = filters || appliedFilters

      let groupIds = [userCtx.session.user.organization_id]
      //Negative Ids causes exceptions on HelloSign API, only send valid alias values
      if (userCtx.session.user.organization_alias > 0) {
        groupIds.push(userCtx.session.user.organization_alias)
      }

      // Build request
      const requestBody = {
        groupIds: groupIds,
        pageNum: page || 1,
        pageSIze: pageSize || 101, //Default to full listing which is anything larger than 100
      }

      if (useFilters.dateCreated) {
        let eopFilter = !!useFilters.dateCreated[1]
          ? useFilters.dateCreated[1]
          : useFilters.dateCreated[0]
        eopFilter.setHours(23)
        eopFilter.setMinutes(59)
        eopFilter.setSeconds(59)

        requestBody.createDateFilter = [
          DateTime.fromJSDate(useFilters.dateCreated[0]).toUTC().toISO(),
          DateTime.fromJSDate(eopFilter).toUTC().toISO(),
        ]
      }

      if (useFilters.search) {
        requestBody.searchFilter = useFilters.search
      }

      // Execute request
      let data = await docSigningApiListTemplatesPost(
        userCtx.tokens.getDocumentSignToken(),
        requestBody,
      )

      // preProcess some fields
      let newItems = []
      data.items.forEach((item) => {
        item = {
          ...item.template,
          additionalOrganizations: item.additionalOrganizations,
        }

        item.templateCreatedBy =
          stripMetaKey(item?.metadata?.CreatedBy)?.split("|")[0] ||
          "Unknown creator"
        item.templateCreatedOn = new Date(
          stripMetaKey(item?.metadata?.CreatedAt),
        )

        newItems = [...newItems, item]
      })
      data.items = newItems

      //Sort data
      data = sortTemplates(
        appliedFilters.sort,
        appliedFilters.sortDirection,
        data,
      )

      // Set data
      setTemplates(data)
      setLoading(false)
      return data
    } catch (error) {
      console.error(error)
      userCtx.setNotificationError(
        "Unable to list templates. See console for details.",
      )
      setTemplates([])
      setLoading(false)
    }
  }

  function sortTemplates(sortOption, sortDirection, templates) {
    let sortedTemplated = JSON.parse(JSON.stringify(templates))
    sortedTemplated.items.forEach((template) => {
      template.templateCreatedOn = new Date(template.templateCreatedOn)
    })

    let sortField = "templateCreatedOn"
    switch (sortOption) {
      case templateSortOptions[1]:
        sortField = "title"
        break
      case templateSortOptions[2]:
        sortField = "templateCreatedBy"
        break
      default:
        break
    }

    if (sortDirection === "Descending") {
      sortedTemplated.items.sort((a, b) => {
        const result =
          a[sortField] > b[sortField] ? -1 : a[sortField] < b[sortField] ? 1 : 0
        return result
      })
    } else {
      sortedTemplated.items.sort((a, b) => {
        const result =
          a[sortField] < b[sortField] ? -1 : a[sortField] > b[sortField] ? 1 : 0
        return result
      })
    }

    return sortedTemplated
  }

  async function EditTemplateHandler(templateId) {
    setLoading(true)
    try {
      const res = await docSigningApiEditTemplateGet(
        userCtx.tokens.getDocumentSignToken(),
        templateId,
      )
      if (res && res.embedded?.editUrl) {
        const client = new HelloSign({
          clientId: helloSignConfig.clientId,
          skipDomainVerification: helloSignConfig.mode === "Test",
        })
        client.open(res.embedded.editUrl)
        client.on("createTemplate", (data) => {
          userCtx.setNotificationInformation(
            "Your template is being updated in the background. You'll be notified when it's ready.",
          )
        })
      } else {
        userCtx.setNotificationError("Unable to retrieve edit URL.")
        setLoading(false)
      }
    } catch (error) {
      userCtx.setNotificationError(
        "Unable to edit template. See console for details.",
      )
      console.error(error)
    }
    setLoading(false)
  }

  async function DeleteTemplateHandler(template, confirmed = false) {
    if (confirmed) {
      try {
        if (!deleteTemplateIds.includes(template.templateId)) {
          setDeleteTemplateIds([...deleteTemplateIds, template.templateId])

          await docSigningApiDeleteTemplateGet(
            userCtx.tokens.getDocumentSignToken(),
            template.templateId,
          )
          // Notify
          userCtx.setNotificationInformation(
            "The template deletion request was successfully submitted and will be processed as soon as possible",
          )
        }
      } catch (error) {
        let message = "Unable to delete template. See console for details."

        // Check for specific error messages
        if (error.message.toLowerCase().includes("template not found")) {
          message = "Unable to delete template. Template not found."
        }

        userCtx.setNotificationError(message)
        console.error(error)
      }
    } else {
      confirmDialog({
        message: "Are you sure you want to delete this template?",
        header: "CoDelete Template",
        accept: () => DeleteTemplateHandler(template, true),
      })
    }
  }

  async function GetHelloSignConfig() {
    try {
      const config = await docSigningApiHelloSignConfigGet(
        userCtx.tokens.getDocumentSignToken(),
      )
      setHelloSignConfig(config)
    } catch (error) {
      console.error(error)
      userCtx.setNotificationError(
        "Unable to retrieve Hello Sign configuration. See console for details.",
      )
    }
  }

  useEffect(() => {
    GetTemplates()
    GetHelloSignConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.user.organization_id])

  useEffect(() => {
    if (!helloSignConnection) {
      setHelloSignConnection(setupSignalRConnection())
    }
    // Monitor for SignalR errors
    if (hubError) {
      LogError(
        "SignalR Error",
        hubError,
        `\nConnection State: ${hubConnectionState}`,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helloSignConnection, hubError])

  useEffect(() => {
    if (topScroll.current) {
      topScroll.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [topScroll])

  function FilterButton({ iconName, toolTip, disabled, onClickCallBack }) {
    return (
      <button
        type="button"
        className={`btn t-tip ${docSignStyles.filterHeaderButton}`}
        data-pr-tooltip={toolTip}
        data-pr-position="bottom"
        onClick={(e) => {
          e.stopPropagation()
          if (!!onClickCallBack) {
            onClickCallBack()
          }
        }}
        disabled={disabled}
      >
        <span className={`material-icons`}>{iconName}</span>
      </button>
    )
  }

  function LayoutButton({ iconName, toolTip, value }) {
    return (
      <button
        type="button"
        className={`btn t-tip ${docSignStyles.filterHeaderButton} ${
          layoutOption === value ? docSignStyles.layoutButtonSelected : ""
        }`}
        data-pr-tooltip={toolTip}
        data-pr-position="bottom"
        onClick={(e) => {
          e.stopPropagation()
          setLayoutOption(value)
        }}
      >
        <span className={`material-icons`}>{iconName}</span>
      </button>
    )
  }

  function FilterHeader() {
    return (
      <div className={docSignStyles.filterHeaderGridContainer}>
        <div className={docSignStyles.filterHeaderGridTitle}>
          <label>Filters & Sorting</label>
        </div>
        <div className={docSignStyles.filterHeaderGridButtons}>
          <div className={docSignStyles.filterButtonContainer}>
            <LayoutButton
              iconName="menu"
              toolTip="Display templates as a table"
              value="table"
            />
            <LayoutButton
              iconName="apps"
              toolTip="Display templates as a grid"
              value="grid"
            />
          </div>
          <div className={docSignStyles.filterButtonContainer}>
            <FilterButton
              iconName="autorenew"
              toolTip="Refresh page"
              onClickCallBack={() => {
                GetTemplates()
              }}
            />
            <FilterButton
              iconName="add"
              toolTip="Add new template"
              onClickCallBack={() => {
                setShowNewTemplate(true)
              }}
              disabled={!helloSignConfig && !loadingOrganizations}
            />
          </div>
        </div>
      </div>
    )
  }

  function stripMetaKey(value) {
    try {
      return value.split("||")[0]
    } catch (error) {
      // console.warn(`Unable to extract "key" from "value" : ${value}`);
      return value
    }
  }

  function Views() {
    if (templates?.items?.length > 0) {
      if (layoutOption === "grid") {
        return (
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {templates.items.map((template) => (
              <TemplateViewCard
                template={template}
                deletingTemplateIds={deleteTemplateIds}
                key={template.templateId}
                stripMetaKey={stripMetaKey}
                onEditTemplate={EditTemplateHandler}
                onShowTemplate={(template) => {
                  setTemplateInfo(template)
                  setShowTemplateInfo(true)
                }}
                onEditOrganizations={() => {
                  setTemplateInfo(template)
                  setShowOrganizationEdit(true)
                }}
                onDeleteTemplate={DeleteTemplateHandler}
                organizations={organizations}
              />
            ))}
          </div>
        )
      } else {
        return (
          <TemplateViewTable
            templates={templates}
            deletingTempalateIds={deleteTemplateIds}
            stripMetaKey={stripMetaKey}
            appliedSorting={{
              sortField:
                appliedFilters.sort === "Creator name"
                  ? "templateCreatedBy"
                  : appliedFilters.sort === "Template name"
                    ? "title"
                    : "templateCreatedOn",
              sortOrder: appliedFilters.sortDirection === "Descending" ? -1 : 1,
            }}
            onEditTemplate={EditTemplateHandler}
            onShowTemplate={(template) => {
              setTemplateInfo(template)
              setShowTemplateInfo(true)
            }}
            onEditOrganizations={(template) => {
              setTemplateInfo(template)
              setShowOrganizationEdit(true)
            }}
            onDeleteTemplate={DeleteTemplateHandler}
            onSortTemplates={(fieldName, sortOrder) => {
              let sortOption = "Creation date" //CreationDate

              switch (fieldName) {
                case "templateCreatedBy":
                  sortOption = "Creator name"
                  break
                case "title":
                  sortOption = "Template name"
                  break
                default:
                  break
              }

              const direction =
                sortOrder === -1 || sortOrder === -0
                  ? "Descending"
                  : "Ascending"

              setAppliedFilters({
                dateCreated: appliedFilters.dateCreated,
                search: appliedFilters.search,
                sort: sortOption,
                sortDirection: direction,
              })

              setTemplates(sortTemplates(sortOption, direction, templates))
            }}
            organizations={organizations}
          />
        )
      }
    } else {
      return <>No templates found</>
    }
  }

  useClientMethod(helloSignConnection, "HelloSignEvent", (data) => {
    handleHelloSignEvent(
      data,
      userCtx.tokens.getDocumentSignToken(),
      userCtx.setNotificationError,
      userCtx.setNotificationInformation,
      userCtx.session.user.organization_id,
      GetTemplates,
    )
  })

  return (
    <div ref={topScroll}>
      <PageSidebarContent>
        <EnterpriseLibrarySidebar />
      </PageSidebarContent>
      <div className="main-content">
        <Tooltip target=".t-tip" />
        <h1>Document signing: Template management</h1>
        <NewTemplateForm
          showNewTemplate={showNewTemplate}
          onSetShowNewTemplate={setShowNewTemplate}
          helloSignConfig={helloSignConfig}
          organizations={organizations}
        />
        <TemplateInfoDialog
          showTemplate={showTemplateInfo}
          template={templateInfo}
          stripMetaKey={stripMetaKey}
          onDone={() => {
            setShowTemplateInfo(false)
          }}
        />
        <EditTemplateGroups
          showDialog={showOrganizationEdit}
          onSetShowDialog={setShowOrganizationEdit}
          template={templateInfo}
          organizations={organizations}
          onChangesSubmitted={GetTemplates}
        />
        <Accordion style={{ marginBottom: "10px" }} activeIndex={0}>
          <AccordionTab header={FilterHeader()} disabled={loading}>
            <TemplateFilters
              filters={appliedFilters}
              sortOptions={templateSortOptions}
              onChangeSortField={(option) => {
                const sortedTemplates = sortTemplates(
                  option.sort,
                  option.sortDirection,
                  templates,
                )
                setTemplates(sortedTemplates)

                let updatedFilters = appliedFilters
                updatedFilters.sort = option.sort
                updatedFilters.sortDirection = option.sortDirection
                setAppliedFilters(updatedFilters)
              }}
              onChangeFilters={(filters) => {
                if (
                  filters.dateCreated !== appliedFilters.dateCreated ||
                  filters.search !== appliedFilters.search
                ) {
                  setAppliedFilters(filters)
                  GetTemplates(undefined, undefined, filters)
                }

                if (filters.sort !== appliedFilters.sort) {
                  setAppliedFilters(filters)
                  setTemplates(
                    sortTemplates(
                      filters.sort,
                      filters.sortDirection,
                      templates,
                    ),
                  )
                }
              }}
            />
          </AccordionTab>
        </Accordion>
        {loading ? (
          <LoaderMedium />
        ) : (
          <>
            <Views />
            {/* <Paginator
              first={templates?.pageSize * (templates?.pageNum - 1) || 0}
              rows={templates?.pageSize || 10}
              totalRecords={templates?.numResults || 0}
              rowsPerPageOptions={[10, 20, 30]}
              onPageChange={(event) => {
                GetTemplates(event.page + 1, event.rows)
              }}
            /> */}
          </>
        )}
      </div>
    </div>
  )
}
